/**
 * THE MONTH'S TWO MEASURES, as the server reports them (CONTRACT5 §5 + builder A's CHANGES).
 *
 *   chat     a bar: how much of this month's chat is used (a ratio of the plan's allowance), and when it comes back
 *   reports  a number: how many of this month's reports are left, plus any extras bought
 *
 * Pure data and display arithmetic only. The frontend NEVER computes a limit: every state ('ok' | 'low' | 'out') is
 * the server's, every count is the server's, and the dollar allowance never reaches the page (only ratios do). What
 * lives here is how a ratio is printed ("38% used"), how a date is printed ("Oct 1"), and the one rounding rule for
 * the low state ("About 20% of this month's chat left": rounded DOWN to 5, never 0 while chat isn't out).
 *
 * No React here, so the public pages and the tests can import it.
 */

export type MeterState = 'ok' | 'low' | 'out';
export type ExtraKind = 'chat' | 'reports';
export type PlanTier = 'free' | 'plus' | 'pro' | 'studio';

export interface ChatUsage {
  /** Share of the PLAN's allowance used (0..1+; above 1 when extras are being used). */
  used_ratio: number;
  state: MeterState;
  /** ISO date the month's chat comes back (the 1st of next month). */
  resets_on: string;
  /** "about 120 questions a month" — the server's words; the bar is the truth, N is always "about". */
  allowance_note: string;
  /** Extra chat bought this month, as a share of the plan's allowance (0 when none). */
  extra_ratio: number;
}

export interface ReportsUsage {
  /** Every report charged this month (plan's own and extras). */
  used: number;
  /** The plan's reports a month. */
  included: number;
  /** Paid extra reports still unused (kept 12 months). */
  extras_left: number;
  state: MeterState;
  resets_on: string;
  /** Left in total (plan + extras). Additive in builder A's CHANGES; absent from an older server. */
  left?: number;
  /** Left of the plan's own. Additive in builder A's CHANGES. */
  included_left?: number;
}

export interface PlanBlock { tier: PlanTier; name: string; next_tier: 'plus' | 'pro' | null }

export interface ExtraOffer { kind: ExtraKind; price_usd: number; adds: string; label?: string }

export interface UsageBlocks {
  chat: ChatUsage;
  reports: ReportsUsage;
  plan: PlanBlock;
  /** What "keep going" costs; absent means the extras checkout hasn't shipped: never offer it. */
  extra_offers: Partial<Record<ExtraKind, ExtraOffer>>;
}

const STATES: MeterState[] = ['ok', 'low', 'out'];
const TIERS: PlanTier[] = ['free', 'plus', 'pro', 'studio'];
const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const str = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

function chatOf(v: unknown): ChatUsage | null {
  const c = v as Partial<ChatUsage> | null;
  if (!c || !num(c.used_ratio) || !STATES.includes(c.state as MeterState) || !str(c.resets_on)) return null;
  return { used_ratio: Math.max(0, c.used_ratio), state: c.state as MeterState, resets_on: c.resets_on,
    allowance_note: typeof c.allowance_note === 'string' ? c.allowance_note : '', extra_ratio: num(c.extra_ratio) ? Math.max(0, c.extra_ratio) : 0 };
}

function reportsOf(v: unknown): ReportsUsage | null {
  const r = v as Partial<ReportsUsage> | null;
  if (!r || !num(r.used) || !num(r.included) || !STATES.includes(r.state as MeterState) || !str(r.resets_on)) return null;
  return { used: r.used, included: r.included, extras_left: num(r.extras_left) ? r.extras_left : 0, state: r.state as MeterState,
    resets_on: r.resets_on, ...(num(r.left) ? { left: r.left } : {}), ...(num(r.included_left) ? { included_left: r.included_left } : {}) };
}

function planOf(v: unknown): PlanBlock | null {
  const p = v as Partial<PlanBlock> | null;
  if (!p || !TIERS.includes(p.tier as PlanTier) || !str(p.name)) return null;
  const next = p.next_tier === 'plus' || p.next_tier === 'pro' ? p.next_tier : null;
  return { tier: p.tier as PlanTier, name: p.name, next_tier: next };
}

/** What "keep going" costs, from a usage-summary's `extra_offers` or GET /tiers' `extras` (only well-formed offers). */
export function parseExtraOffers(v: unknown): Partial<Record<ExtraKind, ExtraOffer>> | null {
  if (!v || typeof v !== 'object') return null;
  const out: Partial<Record<ExtraKind, ExtraOffer>> = {};
  (['chat', 'reports'] as ExtraKind[]).forEach(kind => {
    const o = (v as Record<string, Partial<ExtraOffer> | undefined>)[kind];
    if (o && num(o.price_usd) && o.price_usd > 0 && str(o.adds)) {
      out[kind] = { kind, price_usd: o.price_usd, adds: o.adds, ...(str(o.label) ? { label: o.label } : {}) };
    }
  });
  return out;
}

/**
 * The v5 blocks from a usage-summary (or an extras-confirm) reply — or null when the server doesn't send them yet.
 * THE GATE: the chat bar, the reports stub, Run's cost and the extras buttons render only when this is non-null.
 */
export function parseUsageBlocks(data: unknown): UsageBlocks | null {
  const d = data as Record<string, unknown> | null;
  if (!d || typeof d !== 'object') return null;
  const chat = chatOf(d.chat);
  const reports = reportsOf(d.reports);
  const plan = planOf(d.plan);
  const extra_offers = parseExtraOffers(d.extra_offers);
  if (!chat || !reports || !plan || !extra_offers) return null;
  return { chat, reports, plan, extra_offers };
}

// ── the chat bar ─────────────────────────────────────────────────────────────────────────────────

/** "38% used": the share of this month's WHOLE chat (plan + extras) that is used, 0..100. Display arithmetic only. */
export function chatPercent(chat: Pick<ChatUsage, 'used_ratio' | 'extra_ratio'>): number {
  const whole = 1 + Math.max(0, chat.extra_ratio);
  return Math.max(0, Math.min(100, Math.round((100 * chat.used_ratio) / whole)));
}

/** How far along the strip the ink runs (0..1), and where the plan's own share ends when extras were bought. */
export function inkShare(chat: Pick<ChatUsage, 'used_ratio' | 'extra_ratio'>): { ink: number; notch: number | null } {
  const whole = 1 + Math.max(0, chat.extra_ratio);
  const ink = Math.max(0, Math.min(chat.used_ratio, whole) / whole);
  return { ink, notch: chat.extra_ratio > 0 ? 1 / whole : null };
}

/** The low state's words: the share LEFT, rounded down to 5 and never under 5 ("About 20% of this month's chat left"). */
export function lowLeftPercent(chat: Pick<ChatUsage, 'used_ratio' | 'extra_ratio'>): number {
  const left = 100 - chatPercent(chat);
  return Math.max(5, Math.floor(left / 5) * 5);
}

// ── the reports tally ────────────────────────────────────────────────────────────────────────────

/** Reports left of the plan's own this month (the server's, or derived from its counts on an older reply). */
export function includedLeft(r: ReportsUsage): number {
  if (num(r.included_left)) return Math.max(0, r.included_left);
  if (num(r.left)) return Math.max(0, r.left - r.extras_left);
  return Math.max(0, r.included - r.used);
}
/** Reports left in total: the plan's own plus unused extras. */
export function reportsLeft(r: ReportsUsage): number {
  return num(r.left) ? Math.max(0, r.left) : includedLeft(r) + Math.max(0, r.extras_left);
}

// ── dates ────────────────────────────────────────────────────────────────────────────────────────

const localDay = (iso: string): Date | null => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "Oct 1" from an ISO date (the server's resets_on), or null when there is none to print. */
export function dayLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = localDay(iso);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
}

/**
 * A billing date ("Oct 14", or "Jan 3, 2027" outside this year) from the server's ISO day or timestamp. A timestamp is
 * read as its UTC day (Stripe's billing clock), so every timezone prints the same day. Anything before 2020 — an epoch
 * zero from an unset period — is no date at all: the caller omits the row.
 */
export function billDay(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso || typeof iso !== 'string') return null;
  let y: number; let m: number; let d: number;
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    [y, m, d] = iso.split('-').map(Number);
    m -= 1;
  } else {
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return null;
    y = t.getUTCFullYear(); m = t.getUTCMonth(); d = t.getUTCDate();
  }
  if (!Number.isFinite(y) || y < 2020) return null;
  const local = new Date(y, m, d);
  if (Number.isNaN(local.getTime())) return null;
  return local.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(y !== now.getFullYear() ? { year: 'numeric' } : {}) });
}

/** Whole days from today until an ISO date (0 when it's today or past). */
export function daysUntil(iso: string, now: Date = new Date()): number {
  const d = localDay(iso);
  if (!d) return 0;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((d.getTime() - today.getTime()) / 86400000));
}

/** "in 5 days" / "tomorrow" / "today". */
export function inDays(n: number): string {
  if (n <= 0) return 'today';
  if (n === 1) return 'tomorrow';
  return `in ${n} days`;
}

/** The day before a reset date: the last day a chat extra bought this month lasts ("Sep 30"), and how many days that is. */
export function extraChatLasts(resetsOn: string, now: Date = new Date()): { label: string; days: number } | null {
  const reset = localDay(resetsOn);
  if (!reset) return null;
  const last = new Date(reset.getFullYear(), reset.getMonth(), reset.getDate() - 1);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.max(1, Math.round((last.getTime() - today.getTime()) / 86400000) + 1);
  return { label: last.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), days };
}

/** "September": the month a chat extra is for, from the month before the reset date. */
export function extraChatMonth(resetsOn: string): string | null {
  const reset = localDay(resetsOn);
  if (!reset) return null;
  return new Date(reset.getFullYear(), reset.getMonth() - 1, 1).toLocaleDateString('en-US', { month: 'long' });
}

/** The burst guard lifts at the next 00:00 UTC; said in the creator's own clock ("5 PM", "7:30 PM"). */
export function burstLiftsAt(now: Date = new Date()): string {
  const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
  const withMinutes = next.getMinutes() !== 0;
  return next.toLocaleTimeString('en-US', { hour: 'numeric', ...(withMinutes ? { minute: '2-digit' } : {}) });
}

/** "$5" / "$10" from an offer's price. */
export const money = (usd: number): string => `$${Number.isInteger(usd) ? usd : usd.toFixed(2)}`;

/** "about 50 questions" → "about 50 more questions"; "5 reports" → "5 more reports" (an offer's own words). */
export const moreOf = (adds: string): string => adds.replace(/^((?:about )?[\d,]+) /i, '$1 more ');

/** "About 120 questions a month" — the server's note, capitalised. */
export const capitalise = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// ── the server's refusals, recognised by their words ─────────────────────────────────────────────
// The job layer hands the composer a refusal's MESSAGE only (backgroundJobsService throws detail.message), so the
// two limit refusals and the daily burst guard are recognised by the plain words the server writes (builder A's
// CHANGES). Recognised, they are never printed as sent: the limit becomes the note, the burst guard the page's own
// words in the creator's clock (never "midnight UTC").
export type Refusal = 'chat_limit' | 'reports_limit' | 'burst' | null;
export function refusalOf(message: string | null | undefined): Refusal {
  if (!message) return null;
  const t = message.trim();
  if (/You[’']ve used this month[’']s chat\b/.test(t)) return 'chat_limit';
  if (/You[’']ve used your \d+ reports this month\b/.test(t)) return 'reports_limit';
  if (/You[’']ve asked a lot today\b/.test(t)) return 'burst';
  return null;
}

// ── a question that reads a lot ──────────────────────────────────────────────────────────────────
/** A paste longer than this reads a lot (words only: the server never says how much a question costs before it runs). */
export const LONG_PASTE_CHARS = 4000;
/** Three or more Gmail threads ticked reads a lot. */
export const MANY_THREADS = 3;

/**
 * Whether the next question reads a lot, and what: an input heuristic only (the page never prices a question), said in
 * plain words before Ask — "3 email threads and a long paste" / "a negotiation". Null when it's an ordinary question.
 */
export function heavyReads({ gmailThreads, chars, negotiate }: { gmailThreads: number; chars: number; negotiate: boolean }): string | null {
  const parts: string[] = [];
  if (gmailThreads >= MANY_THREADS) parts.push(`${gmailThreads} email threads`);
  if (chars > LONG_PASTE_CHARS) parts.push('a long paste');
  if (negotiate) parts.push('a negotiation');
  if (!parts.length) return null;
  return parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}
